import moment from 'moment';
import {
  camelCase,
  capitalize,
  isEmpty,
  range,
  round,
} from 'lodash';

import AsyncStorage from './AsyncStorage';
import { CASE_CONVERTER_EXCEPTIONS, FORMAT_INPUT_FIELDS } from './constants';
import {
  DOC_UPLOAD_BANNER_ERROR_MESSAGES,
  DOC_UPLOAD_ERROR_MESSAGES,
  FILE_TYPE_REASON_CODE,
  MAX_FILES_REASON_CODE,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_REASON_CODE,
} from './docUpload/constants';
import { logDownloadGA } from './googleAnalyticsLogging';

/**
 * @description Creates array buffer for attachment to blob conversion
 * @param {string} base64 base64 string data
 * @return {array} Array buffer
 */
export const base64ToArrayBuffer = (base64) => {
  const binaryString = window.atob(base64);
  const binaryLen = binaryString.length;
  const bytes = new Uint8Array(binaryLen);
  range(binaryLen).forEach((i) => {
    const ascii = binaryString.charCodeAt(i);
    bytes[i] = ascii;
  });
  return bytes;
};

/**
 * @description Takes in an object and deeply changes all keys to camelCase.
 *
 * @param {object} obj the object to camelize
 * @return {any} the passed in object with camelCase keys
 */
export const camelizeKeys = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((v) => camelizeKeys(v));
  }
  if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) =>
      ({
        ...result,
        [camelCase(key)]: camelizeKeys(obj[key]),
      }), {});
  }
  return obj;
};

/**
 * @description function to convert camel-cased string into snake-case
 *
 * @param {string} string Camel-cased string
 * @return {string} Snake-cased string
 */
export const camelToSnakeCase = (string) => {
  if (CASE_CONVERTER_EXCEPTIONS[string]) return CASE_CONVERTER_EXCEPTIONS[string];
  return string
    .replace(/([A-Z])/g, (match) => `_${match}`)
    .replace(/^./, (match) => match.toUpperCase());
};

/**
 * @description capitalizes all words in a string
 *
 * @param {string} str string to convert
 * @return {stringify} formatted string
 */
export const capitalizeString = (str) => {
  if (typeof str !== 'string') return '';
  return str.replace(/\w+/g, capitalize);
};

/**
 * @description convert USD currency string to number
 *
 * @param {string} currencyStr number (integer / float) to convert
 * @return {number} numerical value of currency string
 */
export function convertCurrencyStrToNum(currencyStr) {
  return Number(currencyStr.replace(/[^0-9.-]+/g, ''));
}

/**
 * @description convert number to currency string with $ and commas
 *
 * @param {number} number number (integer / float) to convert
 * @param {integer} digits integer representing number of digits after the decimal
 * @return {stringify} currency string
 */
export function convertNumToCurrencyStr(number, digits = 2) {
  let strNum = 0;
  if (number) {
    strNum = number.toFixed(digits).replace(/[-]/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (number < 0) {
      return `-$${strNum}`;
    }
  }
  return `$${strNum}`;
}

/**
 * @description convert phone number to non-breaking phone number string
 * @param {string}
 * @return {string} formatted string
 */
export const convertToNonBreakingPhoneNumber = (phoneNumber) => (
  phoneNumber.split('-').join('‑').split('—').join('‑'));

/**
 * @description convert number to currency string with commas added (no $ sign)
 *
 * @param {number} number number (integer / float) to convert
 * @param {integer} digits integer representing number of digits after the decimal
 * @return {stringify} currency string
 */
export const convertNumToCustomCurrencyStr = (number, digits = 2) => {
  const strNum = number.toFixed(digits).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${strNum}`;
};

/**
 * @description format dollar sign amount
 *
 * @param {string} amount dollar sign amount with commas
 * @return {string} formatted string
 */
export const formatAmountsWithCommas = (amount) => {
  const amountValue = amount || 0;
  const splitAmount = parseFloat(amountValue).toFixed(2).toString().split('.');
  splitAmount[0] = splitAmount[0].replace(/[-]/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (amountValue < 0) {
    return `-$${splitAmount.join('.')}`;
  }
  return `$${splitAmount.join('.')}`;
};

/**
 * @description format date based on specified output format
 *
 * @param {string} date date string that you need formated
 * @param {string} inputFormat current date string format
 * @param {string} outputFormat format that you want to convert the date string into
 * @return {string} formatted date
 */
export function formatDate(date, inputFormat, outputFormat = 'MM/DD/YYYY') {
  if (inputFormat === 'UTC') {
    return moment(date).format(outputFormat);
  }
  return moment(date, 'YYYY-MM-DD HH:mm:ss').format(outputFormat);
}

/**
 * @description Creates array of form error messages for screen readers
 * @param {object} notifications
 * @return {array} formatted error messages
 */
export const formatErrorMessages = (notifications) => (
  Object.entries(notifications)
    .reduce((acc, [field, message]) => {
      if (message) {
        const formattedField = FORMAT_INPUT_FIELDS[field] || field;
        const formattedMessage =
          `There was an error with the form field ${formattedField}: ${message}.`;
        acc.push(formattedMessage);
      }
      return acc;
    }, [])
);

export const formatFrequencyWithMonths = (number) =>
  (number === 1 ? `${number} month` : `${number} months`);

/**
 * @description formats decimal to percentage
 * @param {number} number as a decimal
 * @return {string} formatted percentage
 */
export const formatPercentage = (num) => `${round(num * 100, 3).toString()}%`;

/**
 * @description formats phone number
 * @param {string} phoneNumberString as a string
 * @return {string} formatted phone number
 */
export const formatPhoneNumber = (phoneNumberString) => {
  const cleaned = (`${phoneNumberString}`).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return ['(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return '';
};

/**
 * Given a DataURL, returns just the data string portion (as opposed to the metadata
 * provided before the first comma). If there is no data portion, returns an empty string.
 * @param {string} dataURL
 * @returns just the data part of the DataUrl
 * @example
 *   'data:image/png;base64,SGVsbG8sIFdvcmxkIQ==' --> 'SGVsbG8sIFdvcmxkIQ=='
 *   'blahblah' --> ''
 */
export const getDataStringFromDataURL = (dataURL) => {
  // Match the rest of the string that comes after the first comma
  const regex = /^[^,]*,(.*)/gs;
  const match = regex.exec(dataURL);
  const dataString = match ? match[1] : '';
  return dataString;
};

/**
 * Given a fileName (or any string), returns file extension. If there is no extension, returns
 * empty string.
 * @param {string} fileName
 * @returns file extension portion of fileName
 * @example
 *   'coolFileName_123.png' --> '.png'
 *   'badFileName_123' --> ''
 */
export const getExtensionFromFileName = (fileName) => {
  // Match the final period with the remaining part of the string after it
  const regex = /^.*(\..*)/gs;
  const match = regex.exec(fileName);
  const extension = match ? match[1] : '';
  return extension;
};

/* Tries to get a JSON item in AsyncStorage and return the parsed value. If it can't be parsed
 * or if it's null, it returns an empty object (in a Promise)
 * @param {string} key AsyncStorage lookup key
 * @returns {Promise<object>} Stored object value (or empty object)
 */
export const getParsedJSONStorageItem = async (key) => {
  try {
    return JSON.parse(await AsyncStorage.getItem(key)) || {};
  } catch {
    return {};
  }
};

export const getQueryParam = (key, searchString, shouldDecode = true) => {
  const queryString = searchString || window.location.search;
  const queryParam = key.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp(`[\\?&]${queryParam}=([^&#]*)`);
  const results = regex.exec(queryString);
  if (results === null) return null;
  const result = results[1].replace(/\+/g, ' ');
  return shouldDecode ? decodeURIComponent(result) : result;
};

export const getStringByteSize = (str) => new Blob([str]).size;

/**
 * @description interpolates string, delimiter is double curly brackets
 * @param {string} str The string to interpolate
 * @param {object} values The object that maps the words to be replaced with their values
 * @return {string} The interpolated string
 * Example: I work for {{company}}. => I work for Dovenmuehle.
 */
export const interpolate = (str, values) =>
  str.replace(/{{(.*?)}}/gm, (match, key) => values[key] || match);

/**
 * @description masks all but the last 4 characters
 * @param {string} value the item being masked
 */
export const maskedCharacterGenerator = (value) => value.replace(/.(?=.{4})/g, '*');

/**
 * @description masks all but the last 4 digits
 * @param {string} value the item being masked
 */
export const maskedNumberGenerator = (value) => value.replace(/.(?=\d{4})/g, '*');

/* eslint-disable max-len */
/**
 * ordinalize
 * @description Converts number to ordinalized string. Implementation taken from
 *              https://stackoverflow.com/a/13627586. A more standardized approach could be to utilize
 *              the Intl api described here: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/PluralRules/PluralRules#using_options
 *              However, Intl is not natively supported by Android.
 * @param {number} number non-negative integer
 * @example
 *  0 -> "0th"
 *  1 -> "1st"
 *  2 -> "2nd"
 *  3 -> "3rd"
 *  11 -> "11th"
 *  21 -> "21st"
 */
/* eslint-enable max-len */
export const ordinalize = (number) => {
  const ones = number % 10;
  const tens = number % 100;
  if (ones === 1 && tens !== 11) {
    return `${number}st`;
  }
  if (ones === 2 && tens !== 12) {
    return `${number}nd`;
  }
  if (ones === 3 && tens !== 13) {
    return `${number}rd`;
  }
  return `${number}th`;
};

/*
 * @description Resolves a message based on a provided intl ID or returns the provided value as-is.
 *
 * @param {function} formatMessage function from react-intl to format messages.
 * @param {string} message id or text string to resolve.
 * @param {Object} messages object containing all localized messages.
 * @param {Object} [values] optional object containing values to interpolate in the message.
 * @returns {string} - The localized message or the original text if no ID is found.
 */
export const resolveIntlMessage = ({
  formatMessage,
  message,
  messages = {},
  values = {},
}) => {
  if (typeof message === 'string' && messages[message]) {
    return formatMessage({ id: message }, values);
  }
  return message;
};

/*
 * @description Takes in an object and deeply changes all keys to snake_case with uppercase.
 *
 * @param {object} obj the object to Snake_Case
 * @return {any} the passed in object with snake case keys
 */
export const snakeCaseKeys = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => snakeCaseKeys(item));
  }
  if (obj !== null && obj !== undefined && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [camelToSnakeCase(key)]: snakeCaseKeys(obj[key]),
      }),
      {},
    );
  }
  return obj;
};

/**
 * @description Fetches encrypted client login data from AsyncStorage for
 * the Mobile Native application Client Select screen.
 * @returns Encrypted client login data
 */
export const getEncryptedClients = async () => AsyncStorage.getItem('clients');

/**
 * @description Stores encrypted client login data in AsyncStorage for
 * the Mobile Native application Client Select screen.
 */
export const setEncryptedClients = async (encryptedClients) =>
  AsyncStorage.setItem('clients', encryptedClients);

/**
 * @description Removes encrypted client login data in the event it's corrupted
 */
export const deleteEncryptedClients = async () => AsyncStorage.removeItem('clients');

/**
 * @description Using the JS native Date object, calculates the current Unix time.
 *
 * @return {number} The current Unix time.
 *
 * For reference, UNIX time is the number of seconds elapsed since the Unix Epoch
 * (January 1, 1970 00:00:00 UTC).
 */
export const getUnixTime = () => Math.floor(Date.now() / 1000);

/**
 * ================================
 * SESSION COOKIE HELPER FUNCTIONS
 * ================================
 */

/**
 * @description Given the name of a specific cookie, `getCookie` will find and return the
 * value of the stored cookie.
 * The regexStr selects the specified cookie and sets the payload within a capture group and
 * returns the capture group.
 * @param {string} cookie Name of the cookie to parse.
 * @return {object} The specified cookie value. If there is no value, an empty object will
 * be returned.
 */
export const getCookie = (cookie) => {
  // eslint-disable-next-line no-useless-escape
  const regexStr = new RegExp(`(?:(?:^|.*;\\s*)${cookie}\\s*\\=\\s*([^;]*).*$)|^.*$`, 'g');
  const cookieValue = document.cookie.replace(regexStr, '$1');
  if (!cookieValue) return '';
  return cookieValue;
};

/**
 * @description Creates/Updates a cookie. The value will be stringified before storing it in the
 * cookie.
 * @param {string} name The name of the cookie.
 * @param {any} value The cookie value. The value will be stringified before adding to the cookie.
 * @param {object} options Options to configure the cookie. Available options can be found here:
 * https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie
 */
export const setCookie = (name, value, options = {}, useRootPath = true) => {
  const baseCookie = `${name}=${value}`;
  const cookieWithOptions = Object.keys(options).reduce((acc, option) => {
    const cookieOption = `; ${option}=${options[option]}`;
    return acc + cookieOption;
  }, baseCookie);
  const cookieWithPath = useRootPath ? `${cookieWithOptions}; path=/` : cookieWithOptions;
  document.cookie = `${cookieWithPath};`;
};

/**
 * @description Removes a cookie from document.cookie by manually expiring the cookie
 * @param {string} cookie Name of the cookie to remove/delete
 */
export const removeCookie = (cookie) => {
  document.cookie = `${cookie}=;expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;`;
};

/**
 * @description Takes in a Snake_Case string and replaces underscores with space characters.
 */
export const removeUnderscores = (string) => string.replace(/_/g, ' ');

/**
 * @description Takes in a camelCase string and adds spaces/capitalizes letters
 */
export const unCamelCase = (str) =>
  str.replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/^./, (val) => val.toUpperCase());

// *** CSV Helpers *** //
/**
 * @description format individual values for CSV row
 *
 * @param {number or string} value
 * @return {string} string value of param
 */
const formatCsvValue = (value) => {
  const innerValue = value === null ? '' : value.toString();
  return `"${innerValue.replace(/"/g, '""')}"`;
};

/**
 * @description Parse each row of data in CSV format. Uses/iterates over the already ordered
 * labels and grabs the value in dataObject and makes sure the unordered data is now in the
 * correct order.
 *
 * @param {array} dataRow, array of headers (strings) for the columns
 * @return {string} string of parsed Data for csv file
 */
export const convertRowToCSV = (orderedLabels, dataRow) => {
  const formattedRow = orderedLabels.map((label) => {
    const value = dataRow ? (dataRow[label] || null) : label;
    return formatCsvValue(value);
  }).join(',');
  return `${formattedRow}\r\n`;
};
// *** End CSV Helpers *** //

/**
 * @description: receives blob data and triggers a download in the browser
 * @param  {Blob} blobData resolved data to download as a file
 * @param  {string} fileName grabbed filename from header
 * @return {void}
 */
export const triggerDownloadBlob = (blobData, fileName) => {
  // Log for analytics
  logDownloadGA(fileName);

  // Actual download logic
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blobData);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
};

// Format text into notification content
export const formatNotificationContent = ({
  buttonPrefixText,
  contentType,
  handleButton,
  noMargin,
  style,
  text,
  type,
}) => {
  const content = { text };

  if (style) {
    content.style = style;
  }

  if (type) {
    content.type = type;
  }

  if (handleButton) {
    content.handleButton = handleButton;
  }

  if (buttonPrefixText) {
    content.buttonPrefixText = buttonPrefixText;
  }

  return {
    inlineBlocks: [content],
    noMargin,
    type: contentType,
  };
};

// Formats service transfer message into notification content
export const getServiceTransferNotification = ({
  handleButton,
  transferStatus: { respaLetterId, servicingSoldDate },
  transferStatusBannerText,
  transferStatusBannerTitle,
}) => {
  const content = () => {
    const contentToUse = formatNotificationContent({
      contentType: 'paragraph',
      text: transferStatusBannerText,
      type: 'span',
    });
    if (respaLetterId) {
      const textButton = formatNotificationContent({
        contentType: 'button',
        handleButton,
        text: 'View Details',
        type: 'button',
      });
      return [contentToUse, textButton];
    }
    return [contentToUse];
  };

  const date = formatDate(servicingSoldDate, 'UTC', 'MMMM D, YYYY');

  return { content: content(), date, name: transferStatusBannerTitle };
};

const base64UrlEncode = (str) => (
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
);

// RETURNS PROMISE
const sha256 = (str) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  return window.crypto.subtle.digest('SHA-256', data);
};

export const generateCodeChallenge = async (verifier) => {
  const digest = await sha256(verifier);
  return base64UrlEncode(digest);
};

export const generateCodeVerifier = () => {
  const randomValues = new Uint32Array(32);
  window.crypto.getRandomValues(randomValues);
  const callbackFxn = (num) => `0${num.toString(16)}`.slice(-2);
  return Array.from(randomValues, callbackFxn).join('');
};

/**
 * @description create a query string from an object
 *
 * @param {object} queryParams
 * @return {string} query string
 */
export const generateQueryString = (queryParams) => (
  Object.keys(queryParams).map((key) => (
    `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`
  )).join('&')
);

export const getLossDraftNotifications = ({
  activeDisbursements,
  claimNumber,
  claimTitle,
  completedDisbursements,
  lossDate,
  reimbursedAmount,
  reimbursedTotal,
}) => {
  const contentToUse = [];
  let dateToUse = lossDate;
  contentToUse.push(formatNotificationContent({
    contentType: 'header-three',
    noMargin: true,
    text: `${claimTitle} Claim`,
    type: 'span',
  }));
  contentToUse.push(formatNotificationContent({
    contentType: 'paragraph',
    text: `Disbursed: ${reimbursedTotal} of ${reimbursedAmount}`,
    type: 'span',
  }));

  activeDisbursements.forEach(({
    actionRequired,
    date,
    formattedDate,
    nextStep: { message },
    subtitle,
    title,
  }) => {
    const activeDisbursementMessage = formatNotificationContent({
      contentType: 'paragraph',
      text: `${actionRequired ? message : subtitle}`,
      type: 'span',
    });
    const dateContent = formatNotificationContent({
      contentType: 'paragraph',
      noMargin: true,
      text: `${formattedDate}`,
      type: 'span',
    });
    const reimbursementContent = formatNotificationContent({
      contentType: 'paragraph',
      noMargin: true,
      style: ['BOLD'],
      text: title,
      type: 'span',
    });
    dateToUse = determineDateToUse(date, dateToUse);
    contentToUse.push(reimbursementContent);
    contentToUse.push(dateContent);
    contentToUse.push(activeDisbursementMessage);
  });

  completedDisbursements.forEach(({
    amount,
    date,
    formattedDate,
    title,
  }) => {
    const checkDate = formatNotificationContent({
      contentType: 'paragraph',
      noMargin: true,
      text: `Check Issued: ${formattedDate}`,
      type: 'span',
    });
    const disbursementAmount = formatNotificationContent({
      contentType: 'paragraph',
      text: `Payment Amount: ${amount}`,
      type: 'span',
    });
    const disbursementType = formatNotificationContent({
      contentType: 'paragraph',
      noMargin: true,
      style: ['BOLD'],
      text: title,
      type: 'span',
    });
    dateToUse = determineDateToUse(date, dateToUse);
    contentToUse.push(disbursementType);
    contentToUse.push(checkDate);
    contentToUse.push(disbursementAmount);
  });
  if (!dateToUse) {
    return {};
  }
  return {
    content: contentToUse,
    date: formatDate(dateToUse, null, 'LL'),
    name: `Claim #: ${claimNumber}`,
    noMargin: true,
  };
};

const determineDateToUse = (date, dateToUse) => {
  if (!dateToUse) {
    return date;
  }
  return moment(date).isAfter(moment(dateToUse)) ? date : dateToUse;
};

// Returns object with whitespace trimmed from string type form values, excl. password fields.
export const trimFormValues = (formValues) => Object.keys(formValues).reduce((acc, key) => {
  if (typeof formValues[key] === 'string' && !['password', 'confirmPassword'].includes(key)) {
    acc[key] = formValues[key].trim();
  } else {
    acc[key] = formValues[key];
  }
  return acc;
}, {});

// Checks if value matches regex pattern for Base64 encoded string
export const validateBase64String = (base64String) => {
  const base64Regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;
  return base64Regex.test(base64String);
};

export const getLossDraftNotificationsWithLinks = ({
  handleButton,
  lossDraftClaims,
}) => lossDraftClaims.reduce((lossDraftNotifications, claim) => {
  const notification = getLossDraftNotifications(claim);
  if (isEmpty(notification)) {
    return lossDraftNotifications;
  }
  const { claimNumber } = claim;
  const replyToButton = formatNotificationContent({
    buttonPrefixText: 'Questions about this claim? ',
    contentType: 'button',
    handleButton: () => handleButton({ claimNumber }),
    noMargin: false,
    text: 'Send a message',
    type: 'button',
  });
  notification.content.push(replyToButton);
  const claimCenterLink = {
    inlineBlocks: [
      {
        text: '',
        type: 'span',
      },
      {
        data: {
          mail: '',
          url: '/lossdraft',
        },
        text: 'Visit Claims Center',
        type: 'link',
      },
    ],
    noMargin: false,
    type: 'paragraph',
  };
  notification.content.push(claimCenterLink);

  return [...lossDraftNotifications, notification];
}, []);

// Secure Message/Loss Draft helper functions
/**
  * @description Takes in a string and determines if it is a 413 'request entity too large' error
  *
  * For payloads >10mb, our server responds with 413, 'request entity too large'. The request
  * doesn't even make it to our route handlers. Our frontend 'request' 'checkStatus' util throws
  * the response statusText as the error for any statusCode >=400 (not including 401).
  *
  * Note: The deployed server might respond slightly different, either capitalizing the response
  * status text or saying 'payload' instead of 'request entity'.
 */
export const isMaxFileSizeError = (error) =>
  typeof error === 'string' && error.toLowerCase().includes('too large');

/**
 * @description Takes in an array of uploaded attachments, and parses out attachments
 * with a 'removed' status and removes any unnecessary attachment keys.
 */
export const parseDocUploadAttachments = (attachments) =>
  attachments.filter(({ removed }) => !removed).map(
    ({
      documentCategory,
      fileData,
      fileName,
      fileSize,
      fileType,
    }) => ({
      documentCategory,
      fileData,
      fileName,
      fileSize,
      fileType,
    }),
  );

/**
 * @description Takes in an array of failedAttachmentsIndices and reasonCodes,
 * to create an array of attachment errors that contain validation error messages
 */
/* eslint-disable array-callback-return, consistent-return */
export const parseDocUploadAttachmentBackendErrors = ({
  failedAttachmentsIndices = [],
  reasons = [],
}) => {
  if (failedAttachmentsIndices.length || reasons.length) {
    if (failedAttachmentsIndices.length) {
      // Back-end validation failed due to invalid file type(s)
      return failedAttachmentsIndices.map((index) => (
        { attachmentError: DOC_UPLOAD_ERROR_MESSAGES[FILE_TYPE_REASON_CODE], index }
      ));
    }
    // Back-end validation failed due too many files uploaded
    return [{ formUploadError: DOC_UPLOAD_BANNER_ERROR_MESSAGES[MAX_FILES_REASON_CODE] }];
  }
  return [];
};

/**
 * @description Takes in an array of attachments and returns an array of file size
 * attachment errors for every file larger than the MAX_FILE_SIZE
 */
export const parseDocUploadAttachmentFrontendErrors = ({ attachments = [] }) =>
  attachments.reduce((acc, { fileSize }, index) => {
    if (fileSize > MAX_FILE_SIZE) {
      acc.push(
        { attachmentError: DOC_UPLOAD_ERROR_MESSAGES[MAX_FILE_SIZE_REASON_CODE], index },
      );
    }
    return acc;
  }, []);
